import React, { useState } from "react";
import toast from "react-hot-toast";

import Modal from "../../../components/modal/Modal";
import Input from "../../../components/form-item/Input";
import Select from "../../../components/form-item/Select";
import {
  AI_PROVIDER_LABELS,
  AiProvider,
  AiSettingPreset,
  AiSettingPresetInput,
} from "../../../app/services/adminAi";

const PROVIDER_OPTIONS = Object.entries(AI_PROVIDER_LABELS).map(
  ([value, name]) => ({ value, name }),
);

export interface SettingPresetModalProps {
  /** 編集対象。未指定なら新規作成（複製時は `initial` + `isDuplicate`）。 */
  initial?: AiSettingPreset | null;
  /** 複製モード（`initial` の値を初期値に新規作成する）。 */
  isDuplicate?: boolean;
  isSaving: boolean;
  onSubmit: (body: AiSettingPresetInput) => void;
  onClose: () => void;
}

/**
 * AI モデル設定プリセットの作成 / 編集モーダル。
 *
 * プロバイダ・モデル名・接続設定（API ベース URL / API キー）・生成パラメータ
 * （temperature / max_tokens / timeout / max_retries）を編集する。
 * API キーはサーバーから返却されないため、編集時は「入力した場合のみ更新、
 * 空欄のままなら変更なし」の挙動にする（クリアしたい場合は Django admin から）。
 */
const SettingPresetModal: React.FC<SettingPresetModalProps> = ({
  initial,
  isDuplicate = false,
  isSaving,
  onSubmit,
  onClose,
}) => {
  const isEdit = Boolean(initial) && !isDuplicate;

  const [name, setName] = useState(
    initial ? (isDuplicate ? `${initial.name} のコピー` : initial.name) : "",
  );
  const [provider, setProvider] = useState<AiProvider>(
    initial?.provider ?? "openai",
  );
  const [modelName, setModelName] = useState(
    initial?.model_name ?? "gpt-4o-mini",
  );
  const [apiBase, setApiBase] = useState(initial?.api_base ?? "");
  const [apiKey, setApiKey] = useState("");
  const [temperature, setTemperature] = useState(
    String(initial?.temperature ?? 0.7),
  );
  const [maxTokens, setMaxTokens] = useState(
    String(initial?.max_tokens ?? 1000),
  );
  const [timeoutSec, setTimeoutSec] = useState(String(initial?.timeout ?? 30));
  const [maxRetries, setMaxRetries] = useState(
    String(initial?.max_retries ?? 2),
  );

  const handleSubmit = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("プリセット名を入力してください");
      return;
    }
    if (!modelName.trim()) {
      toast.error("モデル名を入力してください");
      return;
    }
    const temperatureNum = Number(temperature);
    const maxTokensNum = Number(maxTokens);
    const timeoutNum = Number(timeoutSec);
    const maxRetriesNum = Number(maxRetries);
    if (
      [temperatureNum, maxTokensNum, timeoutNum, maxRetriesNum].some((n) =>
        Number.isNaN(n),
      )
    ) {
      toast.error("生成パラメータには数値を入力してください");
      return;
    }

    const body: AiSettingPresetInput = {
      name: trimmedName,
      provider,
      model_name: modelName.trim(),
      api_base: apiBase.trim(),
      temperature: temperatureNum,
      max_tokens: maxTokensNum,
      timeout: timeoutNum,
      max_retries: maxRetriesNum,
    };
    // API キーは入力された場合のみ送信（省略 = 変更なし）。
    if (apiKey.trim()) {
      body.api_key = apiKey.trim();
    }
    onSubmit(body);
  };

  return (
    <Modal
      title={isEdit ? "プリセットを編集" : "プリセットを作成"}
      description="AI プロバイダ・モデル・生成パラメータの設定一式です。「有効にする」で切り替えるまで実行には使われません。"
      size="default"
      onClose={onClose}
      primaryAction={{
        label: isEdit ? "保存" : "作成",
        onClick: handleSubmit,
        loading: isSaving,
        disabled: isSaving,
      }}
      secondaryAction={{
        label: "キャンセル",
        onClick: onClose,
      }}
    >
      <div className="u-col u-w100 u-gap16">
        <Input
          label="プリセット名"
          option="required"
          placeholder="例: OpenAI 構成"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="u-row u-w100 u-gap8">
          <Select
            label="プロバイダ"
            options={PROVIDER_OPTIONS}
            value={provider}
            onChange={(e) => setProvider(e.target.value as AiProvider)}
          />
          <Input
            label="モデル名"
            option="required"
            placeholder="例: gpt-4o-mini"
            value={modelName}
            onChange={(e) => setModelName(e.target.value)}
          />
        </div>
        <Input
          label="API ベース URL"
          option="optional"
          placeholder="空なら各プロバイダの既定エンドポイントを使用"
          value={apiBase}
          onChange={(e) => setApiBase(e.target.value)}
        />
        <Input
          label="API キー"
          option="optional"
          type="password"
          autocomplete="off"
          placeholder={
            initial?.has_api_key && isEdit
              ? "設定済み（変更する場合のみ入力）"
              : "空なら .env のキーを使用（推奨）"
          }
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
        />
        <div className="u-row u-w100 u-gap8">
          <Input
            label="temperature"
            type="number"
            value={temperature}
            onChange={(e) => setTemperature(e.target.value)}
          />
          <Input
            label="max_tokens"
            type="number"
            value={maxTokens}
            onChange={(e) => setMaxTokens(e.target.value)}
          />
        </div>
        <div className="u-row u-w100 u-gap8">
          <Input
            label="タイムアウト（秒）"
            type="number"
            value={timeoutSec}
            onChange={(e) => setTimeoutSec(e.target.value)}
          />
          <Input
            label="リトライ回数"
            type="number"
            value={maxRetries}
            onChange={(e) => setMaxRetries(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
};

export default SettingPresetModal;
